'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import { getAuth, getAPIToken } from './firebase';
import { User } from 'firebase/auth';

interface FirebaseContextProps {
  signOut: () => void;
  getAPIToken: () => void;
  firebaseUser: User | null | undefined;
}

export const FirebaseContext = createContext<FirebaseContextProps>({
  signOut: () => null,
  getAPIToken: () => null,
  firebaseUser: undefined,
});

interface AuthProviderProps {
  children?: React.ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [firebaseUser, setFirebaseUser] = useState<User | null | undefined>(); // Local signed-in state.

  // Listen to the Firebase Auth state and set the local state.
  useEffect(() => {
    const unregisterAuthObserver = getAuth().onAuthStateChanged((user) => {
      setFirebaseUser(user);
    });
    return () => unregisterAuthObserver(); // Unregister Firebase observers when the component unmounts.
  }, []);

  const signOut = () => {
    return getAuth().signOut();
  };

  const value = { signOut, getAPIToken, firebaseUser };

  return <FirebaseContext.Provider value={value}>{children}</FirebaseContext.Provider>;
};

/**
 * This context should not be consumed directly. Instead, use @instamenu/context-user to access authentication and user information.
 */
export function useFirebaseContext() {
  return useContext(FirebaseContext);
}
